/**
 * Клиент API мини-аппа. Раздел 9.6.
 *
 * [SEC-10.1.1] Строка инициализации передаётся ЗАГОЛОВКОМ, а не телом
 * и не строкой запроса: тело логируется прокси, строка запроса попадает
 * в журнал доступа nginx.
 */
import { getInitData } from '../telegram/call.js'

export const INITDATA_HEADER = 'x-telegram-init-data'

/**
 * Непрозрачный сессионный токен ([SEC-10.1.11]).
 *
 * ⚠️ Живёт ТОЛЬКО в памяти вкладки. В localStorage его класть нельзя:
 * мини-апп открывается во встроенном браузере Telegram, где хранилище
 * переживает закрытие приложения, а сессия по замыслу — нет. Потеря токена
 * при перезагрузке не страшна: initData есть всегда, и сессия выпускается
 * заново одним запросом.
 */
let sessionToken: string | null = null

export function setSessionToken(t: string | null): void { sessionToken = t }
export function hasSessionToken(): boolean { return sessionToken !== null }

export interface ApiError {
  error: string
  message: string
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: ApiError }

export async function api<T>(
  path: string, init: RequestInit = {},
): Promise<ApiResult<T>> {
  try {
    const res = await fetch(path, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        // ⚠️ Отправляются ОБА: Bearer как основной путь, initData — как
        // запасной. Сессия могла протухнуть или быть отозвана, и тогда
        // сервер молча переходит на строку инициализации вместо того,
        // чтобы выкидывать человека из приложения посреди работы.
        // Опасные операции сервер всё равно пропускает только по initData.
        ...(sessionToken !== null ? { authorization: `Bearer ${sessionToken}` } : {}),
        [INITDATA_HEADER]: getInitData(),
        // ⚠️ Content-Type ставится ТОЛЬКО когда тело есть. Заголовок при
        // пустом теле — это заявление «я прислал JSON», которого нет,
        // и сервер вправе ответить 400 ещё до аутентификации. Именно так
        // и ломался POST /api/session: тела у него нет, строка инициализации
        // идёт заголовком.
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
    })
    const text = await res.text()
    let body: unknown = null
    if (text.trim() !== '') {
      try { body = JSON.parse(text) } catch { body = null }
    }
    if (!res.ok) {
      const e = (body ?? {}) as Partial<ApiError>
      return {
        ok: false, status: res.status,
        // Без слов «запрос», «сервер», «метод» (04-voice, 3.3): что случилось → что сделать.
        error: { error: e.error ?? 'unknown', message: e.message ?? 'Не получилось. Попробуйте ещё раз.' },
      }
    }
    return { ok: true, data: body as T }
  } catch {
    // Сеть недоступна. Владельцу — нейтрально: подробности ему ничего не дают,
    // а V16 запрещает выносить внутреннюю кухню наружу.
    return {
      ok: false, status: 0,
      error: { error: 'network', message: 'Нет соединения. Проверьте интернет и попробуйте ещё раз.' },
    }
  }
}

/**
 * Скачать файл (архив месяца в Excel). Отдельно от api<T>: здесь не JSON, а двоичное тело,
 * и забрать его простой ссылкой нельзя — заголовки авторизации <a href> не отправляет.
 */
export async function apiFile(path: string): Promise<{ ok: true; blob: Blob } | { ok: false; message: string }> {
  try {
    const res = await fetch(path, {
      headers: {
        ...(sessionToken !== null ? { authorization: `Bearer ${sessionToken}` } : {}),
        [INITDATA_HEADER]: getInitData(),
      },
    })
    if (!res.ok) {
      let message = 'Не получилось скачать файл. Попробуйте ещё раз.'
      try {
        const body = (await res.json()) as { message?: string }
        if (typeof body.message === 'string') message = body.message
      } catch { /* тело не JSON — оставляем общую фразу */ }
      return { ok: false, message }
    }
    return { ok: true, blob: await res.blob() }
  } catch {
    return { ok: false, message: 'Нет соединения. Проверьте интернет и попробуйте ещё раз.' }
  }
}

export interface Session {
  tg_user_id: number
  role: 'owner' | 'staff'
  /** Подпись роли от сервера: «Владелец», «Менеджер» или «Сотрудник». */
  role_label?: string
  perms?: readonly string[]
  first_name: string | null
  token?: string
  expires_at?: string
}

export async function fetchSession(): Promise<ApiResult<Session>> {
  const r = await api<Session>('/api/session', { method: 'POST' })
  if (r.ok && typeof r.data.token === 'string') setSessionToken(r.data.token)
  return r
}

// ── витрины раздела 9.6 ──────────────────────────────────────────────────────

/**
 * Пара «значение + готовая строка». [F-4.0.11]: строку формирует ЯДРО.
 * Клиент её только печатает — иначе экран и утренний отчёт покажут одно
 * и то же число по-разному.
 */
export interface Shown<T> { value: T | null; display: string | null }

export interface Coverage {
  sku_pcs: number
  cogs_known_pcs: number
  display: string
  gap_display: string | null
}

/** Смысл значения решает сервер; клиент не выбирает цвет по знаку числа (09-charts). */
export type Tone = 'good' | 'attn' | 'risk' | 'fine' | 'neutral'
/** То же, что Shown, но с причиной незнания ([F-4.0.13]): «отчёт WB за этот период ещё не закрыт». */
export interface Known extends Shown<string> { reason: string | null }
export interface Delta { text: string; tone: Tone }
export interface SeriesPoint { key: string; label: string; value: string | null; display: string | null; partial: boolean }
export interface Series { period_display: string; points: SeriesPoint[]; summary: string; note: string | null }
export interface BarRow {
  key: string; label: string; sub: string | null; value: string | null; display: string | null
  role: 'current' | 'previous'; tone: Tone | null
}

export interface PayoutWeek {
  title: string
  period_display: string | null
  prev_period_display: string | null
  current: Known
  previous: Known
  delta: Delta | null
  bars: BarRow[]
  summary: string
}
export interface FinesWeek { title: string; period_display: string | null; current: Known; previous: Known; delta: Delta | null; tone: Tone }
export interface WeekMetric { title: string; total: Known; delta: Delta | null; delta_reason: string | null; series: Series }
export interface Drr {
  title: string
  period_display: string
  value: Known
  sub: string | null
  target: Known | null
  target_reason: string | null
  target_caption: string | null
  scale_max: string | null
  tone: Tone
  summary: string
}

export interface Summary {
  shop_id?: string
  shop?: null
  reason?: string
  /** «вт, 13 октября» — дата магазина словами. */
  date_display?: string
  /** null — роли финансы не показываются ([11.3]). */
  payout_week?: PayoutWeek | null
  fines_week?: FinesWeek | null
  /** Прибыль закрытой недели по калькулятору прибыли; null — деньги человеку не открыты. */
  /** Прибыль недели — факт по отчётам WB и себестоимости; yesterday — вчера (prelim — по оперативной статистике). */
  calc_week?: { period_display: string; profit: Shown<string>; filled: number; sold: number; fines: Shown<string>; note: string; yesterday?: { display: string; prelim: boolean } | null } | null
  /** order_sum и drr — null, если человеку не открыты деньги или реклама. */
  week?: { period_display: string; compare_period_display: string; orders: WeekMetric; order_sum: WeekMetric | null }
  drr?: Drr | null
  coverage?: Coverage | null
}

export interface SkuItem {
  nm_id: number
  vendor_code: string | null
  brand: string | null
  title: string | null
  /** Заказано на сумму за 7 закрытых дней (воронка WB). */
  /** null — человеку не открыты деньги магазина. */
  orders_sum_rub: Shown<string> | null
  orders_count: Shown<number>
  /** null — человеку не открыты остатки. */
  stocks_wb_units: Shown<number> | null
}

export interface SkuList {
  shop?: null
  reason?: string
  /** «6–12 октября» — окно заказов. */
  period_display?: string
  items: SkuItem[]
  total_pcs: number
  truncation: string | null
}

export interface ActionItem {
  id: string
  agent: string
  type: string
  reversibility: string
  target_kind?: string | null
  target_id?: string | null
  proposed_at?: string | null
  decision?: string | null
  decided_at?: string | null
  executed_at?: string | null
  /** До какого момента действие можно отменить (канон 15.1: 7 суток). */
  rollback_until?: string | null
  can_rollback?: boolean
  /** Заголовок предложения агента и что оно даст — словами отчёта. */
  title?: string
  effect?: string | null
  /** «13 октября» — утро отчёта, из которого предложение. */
  date_display?: string
  decision_note?: string | null
  decided_display?: string | null
}

export interface ActionList { shop?: null; reason?: string; items: ActionItem[] }

export interface ReportItem {
  id: string
  agent: string
  local_date: string
  title: string | null
  source: string | null
  validator_codes: string[] | null
}

export interface ReportList { shop?: null; reason?: string; items: ReportItem[] }

export const fetchSummary = (): Promise<ApiResult<Summary>> => api<Summary>('/api/summary')
export const fetchSku = (limit = 50): Promise<ApiResult<SkuList>> =>
  api<SkuList>(`/api/sku?limit=${limit}`)
export const fetchActions = (): Promise<ApiResult<ActionList>> => api<ActionList>('/api/actions')
export const fetchHistory = (): Promise<ApiResult<ActionList>> =>
  api<ActionList>('/api/actions/history')
export const fetchReports = (): Promise<ApiResult<ReportList>> => api<ReportList>('/api/reports')

/** Отчёт агента (S-10): слова — модели или шаблона, уже прошли пост-валидатор. */
export interface ReportOutput {
  headline?: string
  insights?: Array<{ text: string; severity?: string }>
  actions?: Array<{ id?: string; title: string; execution?: string; expected_effect?: string | null; params?: { text?: string | null } | null }>
  risks?: string[]
  missing_data?: Array<string | { what: string; why?: string }>
}
export interface ReportFull {
  id: string; agent: string; local_date: string; title: string; source: string; output: ReportOutput | null
  /** «неделя 5–11 октября», «за 6–12 октября» — период данных отчёта строкой сервера; null — у старых отчётов. */
  period_display?: string | null
  /** Оценка этого человека: «полезно» / «мимо»; null — не оценивал. */
  my_verdict?: 'useful' | 'miss' | null
  /** «пересчитан вручную в 14:05» — у отчёта, пересчитанного по кнопке; null — плановый. */
  manual_display?: string | null
  /** Владельцу можно «Обновить отчёт сейчас». */
  can_rerun?: boolean
}
export const fetchReport = (id: string): Promise<ApiResult<ReportFull>> => api<ReportFull>(`/api/reports/${encodeURIComponent(id)}`)

// ── агенты: S-23, S-24 ───────────────────────────────────────────────────────

export interface AgentSettings {
  agent: string
  title: string
  about: string
  enabled: boolean
  llm_enabled: boolean
  /** «09:00» — время магазина. */
  deliver_at_local_time: string
  /** Только у агента покупателей. */
  max_answer_chars?: { value: number; is_default: boolean }
  last_run: { status: string; at: string | null } | null
}

export interface AgentList { shop?: null; reason?: string; can_edit?: boolean; items: AgentSettings[] }

export interface AgentPatch {
  enabled?: boolean
  llm_enabled?: boolean
  deliver_at_local_time?: string
  max_answer_chars?: number | null
}

export const fetchAgents = (): Promise<ApiResult<AgentList>> => api<AgentList>('/api/agents')
export const patchAgent = (agent: string, body: AgentPatch): Promise<ApiResult<{ ok: true; item: AgentSettings }>> =>
  api<{ ok: true; item: AgentSettings }>(`/api/agents/${encodeURIComponent(agent)}`, { method: 'PATCH', body: JSON.stringify(body) })

// ── «Спросить ИИ»: ответ потоком событий (SSE) ───────────────────────────────

export type ChatEvent =
  | { type: 'thread'; id: string; title: string }
  | { type: 'status'; text: string }
  | { type: 'delta'; text: string }
  | { type: 'done'; message: { id: string; content: string; tools: string[]; ungrounded: string[] }; left: number }
  | { type: 'error'; message: string }

/**
 * Вопрос ИИ; события приходят по мере ответа: что ИИ смотрит, куски текста, итог.
 * Тот же вход, что у api(): Bearer и initData заголовками.
 */
export async function chatSend(body: { thread_id: string | null; text: string }, onEvent: (e: ChatEvent) => void, signal?: AbortSignal): Promise<void> {
  let res: Response
  try {
    res = await fetch('/api/chat/send', {
      method: 'POST', body: JSON.stringify(body), ...(signal ? { signal } : {}),
      headers: {
        ...(sessionToken !== null ? { authorization: `Bearer ${sessionToken}` } : {}),
        [INITDATA_HEADER]: getInitData(),
        'Content-Type': 'application/json',
      },
    })
  } catch {
    onEvent({ type: 'error', message: 'Нет соединения. Проверьте интернет и попробуйте ещё раз.' }); return
  }
  if (!res.ok || res.body === null) {
    let m = 'Не получилось. Попробуйте ещё раз.'
    try { const j = (await res.json()) as { message?: string }; if (typeof j.message === 'string') m = j.message } catch { /* тело не JSON */ }
    onEvent({ type: 'error', message: m }); return
  }
  const reader = res.body.getReader()
  const dec = new TextDecoder()
  let buf = ''
  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buf += dec.decode(value, { stream: true })
      let i: number
      while ((i = buf.indexOf('\n\n')) >= 0) {
        const block = buf.slice(0, i)
        buf = buf.slice(i + 2)
        for (const line of block.split('\n')) {
          if (!line.startsWith('data:')) continue                // «: ping» и прочее — пропускаем
          try { onEvent(JSON.parse(line.slice(5).trim()) as ChatEvent) } catch { /* битый кусок */ }
        }
      }
    }
  } catch {
    onEvent({ type: 'error', message: 'Связь прервалась. Ответ мог сохраниться — откройте разговор ещё раз.' })
  }
}
